import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip, Stack,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, FormControlLabel, Checkbox, Paper, LinearProgress, Fade,
} from '@mui/material';
import {
  Add, Remove, Delete, Check, ArrowBack, ArrowForward, CheckCircle,
} from '@mui/icons-material';
import { useQuoteFlow } from '../../contexts/QuoteFlowContext.jsx';

/* ─────────────────────────── DATA ─────────────────────────── */
export const ROOM_DEFS = [
  {
    key: 'living-room', name: 'Living Room', emoji: '🛋️',
    items: [
      { name: 'Sofa / Couch',            volume: 15, icon: '🛋️' },
      { name: 'Coffee Table',            volume: 4,  icon: '🪵' },
      { name: 'TV Stand',                volume: 6,  icon: '📺' },
      { name: 'Armchair',                volume: 8,  icon: '🪑' },
      { name: 'Side Table',              volume: 2,  icon: '🪑' },
      { name: 'Bookshelf',               volume: 10, icon: '📚' },
      { name: 'Entertainment Center',    volume: 12, icon: '📺' },
      { name: 'Floor Lamp',              volume: 1,  icon: '💡' },
      { name: 'Large Rug',               volume: 2,  icon: '🪴' },
    ],
  },
  {
    key: 'kitchen', name: 'Kitchen', emoji: '🍳',
    items: [
      { name: 'Refrigerator',            volume: 20, icon: '🧊' },
      { name: 'Dishwasher',              volume: 15, icon: '🫧' },
      { name: 'Microwave',               volume: 3,  icon: '📦' },
      { name: 'Kitchen Table',           volume: 8,  icon: '🪵' },
      { name: 'Kitchen Chairs (set of 4)', volume: 6, icon: '🪑' },
      { name: 'Stove / Oven',            volume: 18, icon: '🔥' },
      { name: 'Kitchen Island',          volume: 25, icon: '🪵' },
      { name: 'Bar Stools (2)',           volume: 4,  icon: '🪑' },
    ],
  },
  {
    key: 'bedroom', name: 'Bedroom', emoji: '🛏️',
    items: [
      { name: 'Queen Bed Frame',         volume: 12, icon: '🛏️' },
      { name: 'King Bed Frame',          volume: 16, icon: '🛏️' },
      { name: 'Mattress (Queen)',         volume: 8,  icon: '🛏️' },
      { name: 'Mattress (King)',          volume: 10, icon: '🛏️' },
      { name: 'Dresser',                 volume: 10, icon: '🗄️' },
      { name: 'Nightstand',              volume: 3,  icon: '🪵' },
      { name: 'Wardrobe',                volume: 15, icon: '🚪' },
      { name: 'Chest of Drawers',        volume: 8,  icon: '🗄️' },
      { name: 'Vanity Table',            volume: 6,  icon: '🪞' },
    ],
  },
  {
    key: 'dining-room', name: 'Dining Room', emoji: '🍽️',
    items: [
      { name: 'Dining Table',            volume: 30, icon: '🪵' },
      { name: 'Dining Chair',            volume: 5,  icon: '🪑' },
      { name: 'China Cabinet',           volume: 35, icon: '🗄️' },
      { name: 'Buffet / Sideboard',      volume: 25, icon: '🗄️' },
    ],
  },
  {
    key: 'office', name: 'Home Office', emoji: '💼',
    items: [
      { name: 'Desk',                    volume: 8,  icon: '🪵' },
      { name: 'Office Chair',            volume: 4,  icon: '🪑' },
      { name: 'Filing Cabinet',          volume: 6,  icon: '🗄️' },
      { name: 'Bookshelf',               volume: 10, icon: '📚' },
      { name: 'Computer Setup',          volume: 2,  icon: '💻' },
      { name: 'Printer',                 volume: 1,  icon: '🖨️' },
      { name: 'Safe',                    volume: 3,  icon: '🔒' },
    ],
  },
  {
    key: 'laundry', name: 'Laundry', emoji: '🧺',
    items: [
      { name: 'Washing Machine',         volume: 25, icon: '🌀' },
      { name: 'Dryer',                   volume: 25, icon: '♨️' },
      { name: 'Laundry Basket',          volume: 3,  icon: '🧺' },
    ],
  },
  {
    key: 'garage', name: 'Garage / Storage', emoji: '🏠',
    items: [
      { name: 'Workbench',               volume: 20, icon: '🔧' },
      { name: 'Tool Cabinet',            volume: 12, icon: '🧰' },
      { name: 'Shelving Unit',           volume: 10, icon: '🗄️' },
      { name: 'Bicycle',                 volume: 8,  icon: '🚲' },
      { name: 'Boxes (per 10)',          volume: 15, icon: '📦' },
    ],
  },
];

const ADDITIONAL_SERVICES = [
  { id: 'packing',   name: 'Professional Packing',  description: 'We pack everything for you', price: 200, emoji: '📦' },
  { id: 'storage',   name: 'Temporary Storage',      description: '30 days included',           price: 150, emoji: '🏪' },
  { id: 'cleaning',  name: 'Post-Move Cleaning',     description: 'Deep clean of old home',     price: 180, emoji: '🧹' },
  { id: 'assembly',  name: 'Furniture Assembly',     description: 'Disassemble & reassemble',   price: 120, emoji: '🔧' },
  { id: 'specialty', name: 'Specialty Items',        description: 'Piano, artwork, antiques',   price: 300, emoji: '🎹' },
];

const PHASES = ['Select Rooms', 'Add Items', 'Extra Services'];

/* ─────────────────────────── COMPONENT ─────────────────────────── */
const InventoryStep = () => {
  const { quoteData, updateQuoteData, calculateEstimate } = useQuoteFlow();
  const { inventory, serviceDetails, estimate } = quoteData;

  const [phase, setPhase] = useState(0);
  const [selectedRooms, setSelectedRooms] = useState(inventory.selectedRooms || []);
  const [roomIndex, setRoomIndex] = useState(0);
  const [customDialog, setCustomDialog] = useState(false);
  const [customItem, setCustomItem] = useState({ name: '', volume: 1, quantity: 1, fragile: false });
  const [estimateDetails, setEstimateDetails] = useState(() => estimate?.estimateDetails || {
    bedrooms: serviceDetails?.moveSize === 'studio' ? 'studio' : serviceDetails?.moveSize?.replace('br', '') || '2',
    fullness: 'average',
    boxes: 20,
  });
  const estimateTimer = useRef(null);

  const { bookingDetails } = quoteData;
  const [accessDetails, setAccessDetails] = useState({
    origin: {
      stairs:          bookingDetails?.accessDetails?.origin?.stairs          || false,
      elevator:        bookingDetails?.accessDetails?.origin?.elevator        || false,
      floorNumber:     bookingDetails?.accessDetails?.origin?.floorNumber     || '',
      walkingDistance: bookingDetails?.accessDetails?.origin?.walkingDistance || 'short',
    },
    destination: {
      stairs:          bookingDetails?.accessDetails?.destination?.stairs          || false,
      elevator:        bookingDetails?.accessDetails?.destination?.elevator        || false,
      floorNumber:     bookingDetails?.accessDetails?.destination?.floorNumber     || '',
      walkingDistance: bookingDetails?.accessDetails?.destination?.walkingDistance || 'short',
    },
  });

  const setAccess = (loc, field, value) =>
    setAccessDetails(prev => ({
      ...prev,
      [loc]: { ...prev[loc], [field]: value },
    }));

  useEffect(() => {
    updateQuoteData('bookingDetails', { ...bookingDetails, accessDetails });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessDetails]);

  useEffect(() => {
    updateQuoteData('inventory', { ...inventory, selectedRooms });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRooms]);

  const totalVolume = inventory.items?.reduce((s, i) => s + i.volume * i.quantity, 0) || 0;
  const totalItems  = inventory.items?.reduce((s, i) => s + i.quantity, 0) || 0;

  const toggleRoom = (key) =>
    setSelectedRooms(p => p.includes(key) ? p.filter(k => k !== key) : [...p, key]);

  const getItemKey = (roomKey, itemId) => `${roomKey}:${itemId}`;
  const getQty = (roomKey, item) => inventory.items?.find(i => (i.itemKey || getItemKey(i.room, i.itemId || i.id)) === getItemKey(roomKey, item.itemId || item.id))?.quantity || 0;

  const setQty = (roomKey, item, delta) => {
    const itemId = item.itemId || item.id || item.name;
    const itemKey = getItemKey(roomKey, itemId);
    const cur = getQty(roomKey, { ...item, itemId });
    const next = cur + delta;
    if (next <= 0) {
      updateQuoteData('inventory', { ...inventory, items: (inventory.items||[]).filter(i => (i.itemKey || getItemKey(i.room, i.itemId || i.id)) !== itemKey) });
    } else if (cur === 0) {
      updateQuoteData('inventory', { ...inventory, items: [...(inventory.items||[]), { ...item, id: itemId, itemId, itemKey, quantity: 1, room: roomKey }] });
    } else {
      updateQuoteData('inventory', { ...inventory, items: (inventory.items||[]).map(i => (i.itemKey || getItemKey(i.room, i.itemId || i.id)) === itemKey ? { ...i, quantity: next, qty: next } : i) });
    }
  };

  const removeItem = (item) => {
    const itemKey = item.itemKey || getItemKey(item.room, item.itemId || item.id);
    updateQuoteData('inventory', { ...inventory, items: (inventory.items||[]).filter(i => (i.itemKey || getItemKey(i.room, i.itemId || i.id)) !== itemKey) });
  };

  const addCustomItem = () => {
    if (!customItem.name.trim()) return;
    updateQuoteData('inventory', { ...inventory, items: [...(inventory.items||[]), { ...customItem, room: 'custom' }] });
    setCustomItem({ name: '', volume: 1, quantity: 1, fragile: false });
    setCustomDialog(false);
  };

  const toggleService = (id) => {
    const cur = inventory.additionalServices || [];
    updateQuoteData('inventory', { ...inventory, additionalServices: cur.includes(id) ? cur.filter(s => s !== id) : [...cur, id] });
  };

  const activeRooms  = ROOM_DEFS.filter(r => selectedRooms.includes(r.key));
  const currentRoom  = activeRooms[roomIndex];
  const phaseProgress = phase === 0 ? 5 : phase === 1
    ? Math.round((roomIndex / Math.max(activeRooms.length, 1)) * 60) + 20
    : 100;

  const updateEstimateDetails = (field, value) => {
    const next = { ...estimateDetails, [field]: value };
    setEstimateDetails(next);
    updateQuoteData('estimate', { estimateDetails: next, status: 'DIRTY' });
  };

  const progressiveInputs = JSON.stringify({
    serviceDetails,
    inventory,
    accessDetails,
    estimateDetails,
  });

  useEffect(() => {
    if (!serviceDetails?.origin || !serviceDetails?.destination || !serviceDetails?.moveDate) return undefined;
    if (estimateTimer.current) clearTimeout(estimateTimer.current);
    estimateTimer.current = setTimeout(() => {
      calculateEstimate(estimateDetails);
    }, 700);
    return () => {
      if (estimateTimer.current) clearTimeout(estimateTimer.current);
    };
  // The serialized pricing inputs are the intentional debounce trigger.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressiveInputs]);

  /* ══════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════ */
  return (
    <Box sx={{ px: { xs: 1, md: 4 }, py: { xs: 1, md: 3 }, width: '100%', bgcolor: '#F8FAFC', borderRadius: { xs: 2, md: 3 } }}>

      <Box sx={{ mb: { xs: 3, md: 5 }, display: { xs: 'block', md: 'grid' }, gridTemplateColumns: '1fr 1fr', gap: 5, alignItems: 'end' }}>
        <Box>
        <Typography variant="overline" sx={{ color: '#2563EB', fontWeight: 900, letterSpacing: '.16em' }}>02 · YOUR MOVE</Typography>
        <Typography component="h2" sx={{ fontSize: { xs: '2.8rem', md: '4.6rem' }, lineHeight: .92, letterSpacing: '-.06em', fontWeight: 900, mt: 1.5 }}>What are you<br /><Box component="span" sx={{ color: '#2563EB', fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: 400 }}>moving?</Box></Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 420, mt: 2 }}>Give us as much detail as you like. Inventory improves accuracy, but it’s completely optional.</Typography>
        </Box>
        <Box sx={{ bgcolor: '#0F172A', color: '#fff', p: { xs: 2, md: 2.5 }, borderRadius: 1.5, mt: { xs: 3, md: 0 } }}>
          <Typography variant="overline" sx={{ color: '#93C5FD', fontWeight: 800 }}>PLANNING RANGE</Typography>
          {estimate.status === 'READY' && estimate.range ? <Typography variant="h4" fontWeight={900} sx={{ mt: .5 }}>${estimate.range.min.toLocaleString()} – ${estimate.range.max.toLocaleString()}</Typography> : <Typography variant="body2" sx={{ color: 'rgba(255,255,255,.68)', mt: 1 }}>{estimate.status === 'LOADING' ? 'Updating your range…' : 'Your move details will shape the range.'}</Typography>}
        </Box>
      </Box>

      <Paper elevation={0} sx={{ mb: 2, p: { xs: 1.5, md: 2 }, border: '1px solid #D9E2EC', borderRadius: 1.5, bgcolor: '#fff' }}>
        <Typography variant="overline" color="text.secondary" fontWeight={800}>REFINE THE MOVE</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            select size="small" label="Home size" value={estimateDetails.bedrooms}
            onChange={event => updateEstimateDetails('bedrooms', event.target.value)}
            sx={{ minWidth: { sm: 170 } }}
          >
            {['studio', '2', '3', '4+'].map(value => <MenuItem key={value} value={value}>{value === 'studio' ? 'Studio / 1 bedroom' : `${value} bedrooms`}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="How full?" value={estimateDetails.fullness}
            onChange={event => updateEstimateDetails('fullness', event.target.value)}
            sx={{ minWidth: { sm: 170 } }}
          >
            {['light', 'average', 'full'].map(value => <MenuItem key={value} value={value}>{value[0].toUpperCase() + value.slice(1)}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Approx. boxes" value={estimateDetails.boxes}
            onChange={event => updateEstimateDetails('boxes', Number(event.target.value))}
            sx={{ minWidth: { sm: 150 } }}
          >
            {[5, 15, 30, 50, 70].map(value => <MenuItem key={value} value={value}>{value === 5 ? '0-10' : value === 15 ? '11-20' : value === 30 ? '21-40' : value === 50 ? '41-60' : '60+'} boxes</MenuItem>)}
          </TextField>
        </Stack>
      </Paper>

      {/* Progress header */}
      <Paper elevation={0} sx={{ mb: { xs: 2, md: 3 }, p: { xs: 1.25, md: 2 }, border: '1px solid #D9E2EC', borderRadius: 1.5, bgcolor: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          {PHASES.map((label, i) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{
                width: 24, height: 24, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: i < phase ? 'success.main' : i === phase ? 'primary.main' : 'grey.200',
                color: i <= phase ? 'white' : 'text.disabled',
                fontSize: '0.7rem', fontWeight: 700, transition: 'background-color 0.3s',
              }}>
                {i < phase ? <Check sx={{ fontSize: 14 }} /> : i + 1}
              </Box>
              <Typography variant="caption" fontWeight={i === phase ? 700 : 400}
                color={i === phase ? 'primary.main' : i < phase ? 'success.main' : 'text.disabled'}>
                {label === 'Extra Services' ? 'Extras' : label}
              </Typography>
            </Box>
          ))}
        </Box>
        <LinearProgress variant="determinate" value={phaseProgress} sx={{ height: 6, borderRadius: 3 }} />
      </Paper>

      {/* Running totals */}
      {totalItems > 0 && (
        <Fade in>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
            <Chip label={`${totalItems} item${totalItems !== 1 ? 's' : ''} added`} color="primary" size="small"
              icon={<CheckCircle />} sx={{ fontWeight: 600 }} />
            <Chip label={`${totalVolume} ft³`} variant="outlined" size="small" sx={{ fontWeight: 600 }} />
            <Chip label={totalVolume < 200 ? '🚛 Small Truck' : totalVolume < 500 ? '🚚 Medium Truck' : '🚛 Large Truck'}
              variant="outlined" size="small" color="secondary" sx={{ fontWeight: 600 }} />
          </Box>
        </Fade>
      )}

      {/* ═══════════ PHASE 0 — SELECT ROOMS ═══════════ */}
      {phase === 0 && (
        <Fade in key="p0">
          <Box>
            <Typography variant="h5" fontWeight={700} mb={0.5}>Which rooms are you moving?</Typography>
            <Typography variant="body2" color="text.secondary" mb={{ xs: 2, md: 3 }}>
              Add rooms for a tighter estimate, or skip and refine details with your mover later.
            </Typography>
            <Grid container spacing={{ xs: 1, md: 2 }} sx={{ mb: { xs: 2, md: 4 } }}>
              {ROOM_DEFS.map(room => {
                const active = selectedRooms.includes(room.key);
                return (
                  <Grid item xs={6} sm={4} md={3} key={room.key}>
                    <Card elevation={0} onClick={() => toggleRoom(room.key)} sx={{
                      cursor: 'pointer', border: '1px solid', textAlign: 'center', p: { xs: 1.25, md: 2 },
                      borderColor: active ? '#2563EB' : '#E2E8F0',
                      bgcolor: active ? '#EFF6FF' : '#fff',
                      transition: 'all 0.2s', position: 'relative', borderRadius: 3,
                      '&:hover': { boxShadow: '0 12px 28px rgba(15,23,42,0.08)', borderColor: '#93C5FD' },
                    }}>
                      {active && (
                        <Box sx={{
                          position: 'absolute', top: 8, right: 8,
                          bgcolor: 'primary.main', borderRadius: '50%', width: 20, height: 20,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Check sx={{ fontSize: 12, color: 'white' }} />
                        </Box>
                      )}
                      <Box sx={{ width: { xs: 34, md: 44 }, height: { xs: 34, md: 44 }, mx: 'auto', mb: 1, borderRadius: 2, bgcolor: active ? '#DBEAFE' : '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: { xs: '1.1rem', md: '1.35rem' } }}>{room.emoji}</Box>
                      <Typography variant="subtitle2" fontWeight={600}>{room.name}</Typography>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedRooms.length === 0
                  ? 'Detailed inventory is optional.'
                  : `${selectedRooms.length} room${selectedRooms.length !== 1 ? 's' : ''} selected`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setPhase(2)}
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap', flex: { xs: 1, sm: 'initial' } }}
                >
                  Skip inventory
                </Button>
                <Button variant="contained" size="large" endIcon={<ArrowForward />}
                  disabled={selectedRooms.length === 0}
                  onClick={() => { setRoomIndex(0); setPhase(1); }}
                  sx={{ px: { xs: 2, md: 4 }, fontWeight: 700, flex: { xs: 1, sm: 'initial' } }}>
                  Add Items
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      )}

      {/* ═══════════ PHASE 1 — ITEMS PER ROOM ═══════════ */}
      {phase === 1 && currentRoom && (
        <Fade in key={`p1-${roomIndex}`}>
          <Box>
            {/* Room header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
              <Typography sx={{ fontSize: '2.2rem' }}>{currentRoom.emoji}</Typography>
              <Box>
                <Typography variant="h5" fontWeight={700}>{currentRoom.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Room {roomIndex + 1} of {activeRooms.length} — tap to add items
                </Typography>
              </Box>
            </Box>
            {/* Room dots */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, mt: 1 }}>
              {activeRooms.map((r, i) => (
                <Box key={r.key} onClick={() => setRoomIndex(i)} sx={{
                  width: i === roomIndex ? 28 : 10, height: 10, borderRadius: 5, cursor: 'pointer',
                  bgcolor: i < roomIndex ? 'success.main' : i === roomIndex ? 'primary.main' : 'grey.300',
                  transition: 'all 0.3s',
                }} />
              ))}
            </Box>

            {/* Items grid */}
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {currentRoom.items.map(item => {
                const qty = getQty(currentRoom.key, item);
                return (
                  <Grid item xs={6} sm={4} md={3} key={item.name} sx={{ display: 'flex' }}>
                    <Card elevation={0} sx={{
                      width: '100%', minHeight: 178,
                      border: '1px solid',
                      borderColor: qty > 0 ? '#2563EB' : '#E2E8F0',
                      bgcolor: qty > 0 ? '#EFF6FF' : '#fff',
                      borderRadius: 3,
                      transition: 'all 0.2s', '&:hover': { boxShadow: '0 10px 24px rgba(15,23,42,0.08)' },
                    }}>
                      <CardContent sx={{ p: '12px !important', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'stretch', height: '100%' }}>
                        <Box sx={{ width: 38, height: 38, mx: 'auto', mb: 1, borderRadius: 2, bgcolor: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.15rem' }}>{item.icon}</Box>
                        <Typography variant="caption" fontWeight={600} display="block"
                          sx={{ mb: 1, lineHeight: 1.3, minHeight: 32 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                          {item.volume} ft³
                        </Typography>
                        <Box sx={{ minHeight: 28, mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {qty === 0 ? (
                            <Button size="small" variant="outlined" fullWidth
                              onClick={() => setQty(currentRoom.key, item, 1)}
                              sx={{ minHeight: 28, borderRadius: 2, fontWeight: 600, fontSize: '0.75rem' }}>
                              + Add
                            </Button>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <IconButton size="small" onClick={() => setQty(currentRoom.key, item, -1)}
                                sx={{ bgcolor: 'white', boxShadow: 1, width: 28, height: 28 }}>
                                <Remove sx={{ fontSize: 14 }} />
                              </IconButton>
                              <Typography fontWeight={700} sx={{ minWidth: 20, textAlign: 'center' }}>{qty}</Typography>
                              <IconButton size="small" onClick={() => setQty(currentRoom.key, item, 1)}
                                sx={{ bgcolor: 'primary.main', color: 'white', width: 28, height: 28,
                                  '&:hover': { bgcolor: 'primary.dark' } }}>
                                <Add sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              {/* Custom item card */}
              <Grid item xs={6} sm={4} md={3}>
                <Card elevation={0} onClick={() => setCustomDialog(true)} sx={{
                  border: '2px dashed', borderColor: 'grey.300', cursor: 'pointer',
                  minHeight: 148, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 3, bgcolor: '#fff',
                  transition: 'all 0.2s', '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
                }}>
                  <Box sx={{ textAlign: 'center', p: 1 }}>
                    <Add sx={{ fontSize: 28, color: 'text.disabled', mb: 0.5 }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Custom item
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            </Grid>

            {/* Items added from this room */}
            {inventory.items?.filter(i => i.room === currentRoom.key).length > 0 && (
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'success.50', borderRadius: 2,
                border: '1px solid', borderColor: 'success.200' }}>
                <Typography variant="caption" fontWeight={700} color="success.dark" display="block" mb={1}>
                  ✓ Added from {currentRoom.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {inventory.items.filter(i => i.room === currentRoom.key).map(i => (
                    <Chip key={i.name} label={`${i.name} ×${i.quantity}`} size="small"
                      onDelete={() => removeItem(i)}
                      sx={{
                        fontWeight: 700,
                        color: '#166534',
                        bgcolor: '#F0FDF4',
                        border: '1px solid #86EFAC',
                        '& .MuiChip-deleteIcon': { color: '#16A34A' },
                      }} />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Navigation */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button variant="outlined" startIcon={<ArrowBack />}
                onClick={() => roomIndex === 0 ? setPhase(0) : setRoomIndex(i => i - 1)}>
                {roomIndex === 0 ? 'Back to Rooms' : `← ${activeRooms[roomIndex - 1]?.name}`}
              </Button>
              <Button variant="contained" size="large" endIcon={<ArrowForward />}
                onClick={() => roomIndex < activeRooms.length - 1 ? setRoomIndex(i => i + 1) : setPhase(2)}
                sx={{ px: 3, fontWeight: 700 }}>
                {roomIndex < activeRooms.length - 1
                  ? `Next: ${activeRooms[roomIndex + 1]?.name}`
                  : 'Done — Review Extras'}
              </Button>
            </Box>
          </Box>
        </Fade>
      )}

      {/* ═══════════ PHASE 2 — ADDITIONAL SERVICES ═══════════ */}
      {phase === 2 && (
        <Fade in key="p2">
          <Box>
            <Typography variant="h5" fontWeight={700} mb={0.5}>Any extras?</Typography>
            {inventory.completeness === 'ESTIMATED' && (
              <Typography variant="body2" color="info.main" sx={{ mb: 2, fontWeight: 600 }}>
                Your quote will use the estimate you provided instead of a detailed item list.
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" mb={3}>
              Optional add-ons — pick what you need.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              {ADDITIONAL_SERVICES.map(service => {
                const active = (inventory.additionalServices || []).includes(service.id);
                return (
                  <Grid item xs={12} sm={6} key={service.id}>
                    <Card onClick={() => toggleService(service.id)} sx={{
                      cursor: 'pointer', border: 2, transition: 'all 0.2s',
                      borderColor: active ? 'success.main' : 'grey.200',
                      bgcolor: active ? 'success.50' : 'background.paper',
                      '&:hover': { boxShadow: 3, borderColor: 'success.light' },
                    }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: '16px !important' }}>
                        <Typography sx={{ fontSize: '2rem' }}>{service.emoji}</Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography fontWeight={700}>{service.name}</Typography>
                          <Typography variant="body2" color="text.secondary">{service.description}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography fontWeight={700} color={active ? 'success.main' : 'text.secondary'}>
                            +${service.price}
                          </Typography>
                          {active && <Check sx={{ color: 'success.main', fontSize: 18 }} />}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {/* ── Access Details ── */}
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                🏢 Access Details
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Helps your mover bring the right equipment — affects your quote.
              </Typography>
              <Grid container spacing={2}>
                {[
                  { loc: 'origin',      label: '📍 Pick-up Location'  },
                  { loc: 'destination', label: '🎯 Drop-off Location' },
                ].map(({ loc, label }) => {
                  const acc = accessDetails[loc];
                  const borderColor = loc === 'origin' ? 'primary.200' : 'secondary.200';
                  const bgColor = loc === 'origin' ? 'primary.50' : 'secondary.50';
                  return (
                    <Grid item xs={12} sm={6} key={loc}>
                      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor, bgcolor: bgColor }}>
                        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>{label}</Typography>
                        <Stack spacing={1.5}>
                          {/* Stairs / Elevator toggles */}
                          <Grid container spacing={1}>
                            {[
                              { field: 'stairs',   label: '🪜 Stairs'   },
                              { field: 'elevator', label: '🛗 Elevator' },
                            ].map(({ field, label: tLabel }) => {
                              const on = acc[field];
                              return (
                                <Grid item xs={6} key={field}>
                                  <Box
                                    onClick={() => setAccess(loc, field, !on)}
                                    sx={{
                                      display: 'flex', alignItems: 'center', gap: 1,
                                      p: 1.25, borderRadius: 2, cursor: 'pointer', border: 2,
                                      borderColor: on ? 'primary.main' : 'grey.200',
                                      bgcolor: on ? 'primary.50' : 'white',
                                      transition: 'all 0.15s',
                                    }}
                                  >
                                    <Typography variant="body2" fontWeight={600}
                                      color={on ? 'primary.main' : 'text.secondary'} sx={{ flex: 1 }}>
                                      {tLabel}
                                    </Typography>
                                    {on && <CheckCircle sx={{ fontSize: 16, color: 'primary.main' }} />}
                                  </Box>
                                </Grid>
                              );
                            })}
                          </Grid>

                          {/* Floor number */}
                          <TextField
                            label="Floor number" type="number" size="small" fullWidth
                            value={acc.floorNumber}
                            onChange={e => setAccess(loc, 'floorNumber', e.target.value)}
                            inputProps={{ min: 0, max: 99 }}
                            placeholder="e.g. 3"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
                          />

                          {/* Walking distance */}
                          <Box>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={0.75}>
                              WALKING DISTANCE TO TRUCK
                            </Typography>
                            <Grid container spacing={1}>
                              {[
                                { value: 'short',  label: '< 50 ft',   emoji: '🚶'  },
                                { value: 'medium', label: '50–100 ft', emoji: '🚶‍♂️' },
                                { value: 'long',   label: '100+ ft',   emoji: '🏃'  },
                              ].map(opt => {
                                const sel = acc.walkingDistance === opt.value;
                                return (
                                  <Grid item xs={4} key={opt.value}>
                                    <Box onClick={() => setAccess(loc, 'walkingDistance', opt.value)} sx={{
                                      textAlign: 'center', p: 1, borderRadius: 2, cursor: 'pointer',
                                      border: 2, borderColor: sel ? 'primary.main' : 'transparent',
                                      bgcolor: sel ? 'white' : 'rgba(255,255,255,0.5)',
                                      transition: 'all 0.15s', '&:hover': { bgcolor: 'white', boxShadow: 1 },
                                    }}>
                                      <Typography sx={{ fontSize: '1.1rem' }}>{opt.emoji}</Typography>
                                      <Typography variant="caption" fontWeight={sel ? 700 : 400}
                                        color={sel ? 'primary.main' : 'text.secondary'} display="block">
                                        {opt.label}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            {/* Summary */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, color: '#fff', bgcolor: '#102A43', borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.12)' }}>
              <Typography variant="subtitle1" fontWeight={800} mb={2}>
                Inventory Summary
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)' }} fontWeight={600}>ITEMS</Typography>
                  <Typography variant="h4" fontWeight={800}>{totalItems}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)' }} fontWeight={600}>VOLUME</Typography>
                  <Typography variant="h4" fontWeight={800}>{totalVolume} ft³</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.62)' }} fontWeight={600}>TRUCK SIZE</Typography>
                  <Typography variant="h4" fontWeight={800}>
                    {totalVolume < 200 ? 'Small' : totalVolume < 500 ? 'Medium' : 'Large'}
                  </Typography>
                </Box>
              </Box>
              {inventory.items?.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {inventory.items.map(item => (
                        <Chip key={item.name} label={`${item.name} ×${item.quantity}`} size="small"
                          onDelete={() => removeItem(item)}
                          sx={{
                            fontWeight: 700,
                            color: '#102A43',
                            bgcolor: '#FFFFFF',
                            border: '1px solid rgba(255,255,255,0.72)',
                            '& .MuiChip-deleteIcon': { color: '#627D98' },
                          }} />
                  ))}
                </Box>
              )}
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button variant="outlined" startIcon={<ArrowBack />}
                onClick={() => { setRoomIndex(activeRooms.length - 1); setPhase(1); }}>
                Back to Items
              </Button>
              <Button variant="text" size="small" onClick={() => setPhase(0)} sx={{ color: 'text.secondary' }}>
                Edit room selection
              </Button>
            </Box>
          </Box>
        </Fade>
      )}

      {/* Custom Item Dialog */}
      <Dialog open={customDialog} onClose={() => setCustomDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Add a Custom Item</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField label="Item name" fullWidth value={customItem.name}
              onChange={e => setCustomItem(p => ({ ...p, name: e.target.value }))} />
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>
                APPROXIMATE SIZE
              </Typography>
              <Grid container spacing={1}>
                {[
                  { label: 'Small',  sub: 'Lamp, box',        volume: 3  },
                  { label: 'Medium', sub: 'Desk, chair',       volume: 8  },
                  { label: 'Large',  sub: 'Sofa, dresser',     volume: 15 },
                  { label: 'XL',     sub: 'Fridge, king bed',  volume: 25 },
                ].map(opt => {
                  const sel = customItem.volume === opt.volume;
                  return (
                    <Grid item xs={6} key={opt.label}>
                      <Box onClick={() => setCustomItem(p => ({ ...p, volume: opt.volume }))} sx={{
                        textAlign: 'center', p: 1.5, borderRadius: 2, cursor: 'pointer',
                        border: 2, borderColor: sel ? 'primary.main' : 'grey.200',
                        bgcolor: sel ? 'primary.50' : 'background.paper',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.light' },
                      }}>
                        <Typography variant="subtitle2" fontWeight={700} color={sel ? 'primary.main' : 'text.primary'}>{opt.label}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">{opt.sub}</Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
            <TextField label="Quantity" type="number" fullWidth value={customItem.quantity}
              onChange={e => setCustomItem(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))}
              inputProps={{ min: 1 }} />
            <FormControlLabel
              control={<Checkbox checked={customItem.fragile}
                onChange={e => setCustomItem(p => ({ ...p, fragile: e.target.checked }))} />}
              label="Fragile — needs special handling"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDialog(false)}>Cancel</Button>
          <Button onClick={addCustomItem} variant="contained" disabled={!customItem.name.trim()}>
            Add Item
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryStep;
